import React from 'react';
import CurrencyLabel from 'Utils/CurrencyLabel';

const separatorStyles = {
  wider: {
    borderTop: '2px solid',
    marginTop: '8px',
    paddingTop: '8px',
  },
  doubleBlue: {
    borderTop: '3px double',
    marginTop: '8px',
    paddingTop: '8px',
  },
  simple: {
    borderTop: '1px solid',
    marginTop: '2px',
    paddingTop: '4px',
  },
  default: {
    borderTop: '1px solid',
    marginTop: '1px',
    paddingTop: '2px',
  },
};

const BillingItem = ({ label, value, separatorType, indented, hideContent, bold }) => {
  const separator = separatorStyles[separatorType] || separatorStyles.default;
  const containerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 0',
    ...(indented ? { paddingLeft: '20px', fontSize: '0.9em' } : {}),
    ...separator,
  };

  const labelStyle = {
    fontWeight: bold ? 'bold' : 'normal',
    margin: 0,
  };

  const valueStyle = {
    fontWeight: bold ? 'bold' : 'normal',
  };

  return (
    <div style={containerStyle}>
      {!hideContent && (
        <>
          <label style={labelStyle}>{label}</label>
          <div style={valueStyle}>
            <CurrencyLabel value={value} />
          </div>
        </>
      )}
    </div>
  );
};

const billingLinesDisplayArray = [
  { kind: 'rentalItemTotal', valueKey: 'finalValue', hideIfZero: true },
  { kind: 'purchaseTotal', valueKey: 'finalValue', hideIfZero: true },
  { kind: 'bundleTotal', valueKey: 'finalValue', hideIfZero: true },
  { kind: 'inventoryTotal', valueKey: 'finalValue', separator: 'wider', skipOnBillingTotalsCheck: true },
  { kind: 'staffTotal', label: 'Services Total', valueKey: 'finalValue', hideIfZero: true },
  { kind: 'deliveryTotal', valueKey: 'finalValue', hideIfZero: true },
  { kind: 'damageWaiverFeeTotal', valueKey: 'finalValue', hideIfZero: true },
  { kind: 'globalFeeLine', valueKey: 'finalValue', hideIfZero: true },
  { kind: 'damageFeesTotal', valueKey: 'finalValue', hideIfZero: true, skipOnBillingTotalsCheck: true },
  { kind: 'itemDamageFeeLine', valueKey: 'finalValue', hideIfZero: true, indented: true },
  { kind: 'globalDiscountLine', valueKey: 'finalValue', hideIfZero: true },
  { kind: 'subtotal', valueKey: 'finalValue', separator: 'wider', skipOnBillingTotalsCheck: true, bold: true },
  { kind: 'taxTotal', valueKey: 'finalValue' },
  { kind: 'salesTaxesLine', valueKey: 'finalValue', indented: true, skipOnBillingTotalsCheck: true },
  { kind: 'customTaxesLine', valueKey: 'finalValue', indented: true, skipOnBillingTotalsCheck: true },
  { kind: 'creditCardFeeTotal', valueKey: 'finalValue', hideIfZero: true, label: 'Credit Card Surcharge Fee Total' },
  { kind: 'orderTotal', valueKey: 'finalValue', separator: 'doubleBlue', skipOnBillingTotalsCheck: true, bold: true },
];

const INVENTORY_LINE_KINDS = new Set([
  'rentalItemTotal',
  'purchaseTotal',
  'bundleTotal',
  'inventoryTotal',
]);

const AGGREGATED_INVENTORY_CONFIG = {
  _aggregatedInventoryTotal: true,
  valueKey: 'finalValue',
  separator: 'wider',
};

const BillingLinesTable = ({ billingLines, rentalSetting, truncateLength = 50 }) => {
  if (!billingLines || billingLines.length === 0) {
    return null;
  }

  const findLine = (kind) => billingLines.find((line) => line.kind === kind);
  const rentalItemsTotal = parseFloat(findLine('rentalItemTotal')?.finalValue) || 0;
  const rentalAddOnsTotal = parseFloat(findLine('purchaseTotal')?.finalValue) || 0;
  const rentalBundlesTotal = parseFloat(findLine('bundleTotal')?.finalValue) || 0;
  const inventoryTotalSum =
    rentalItemsTotal + rentalAddOnsTotal + rentalBundlesTotal;
  const aggregateInventoryPrices = Boolean(
    rentalSetting?.displayAggregatedInventoryPrices
  );
  const hasRentalItemLine = Boolean(findLine('rentalItemTotal'));
  const hasPurchaseLine = Boolean(findLine('purchaseTotal'));
  const bundleLine = findLine('bundleTotal');
  const hasVisibleBundleLine =
    bundleLine && (parseFloat(bundleLine.finalValue) || 0) > 0;
  const shouldShowAggregatedInventory =
    aggregateInventoryPrices &&
    (hasRentalItemLine || hasPurchaseLine || hasVisibleBundleLine);

  const displayConfigs = shouldShowAggregatedInventory
    ? [
        AGGREGATED_INVENTORY_CONFIG,
        ...billingLinesDisplayArray.filter((c) => !INVENTORY_LINE_KINDS.has(c.kind)),
      ]
    : billingLinesDisplayArray;

  const renderBillingLinesForKind = (kindConfig) => {
    if (kindConfig._aggregatedInventoryTotal) {
      return [
        <BillingItem
          key="aggregated-inventory-total"
          label="Inventory Total"
          value={inventoryTotalSum}
          separatorType={kindConfig.separator}
          bold={kindConfig.bold}
        />,
      ];
    }
    const matchingLines = billingLines.filter((line) => line.kind === kindConfig.kind);

    return matchingLines.map((line, index) => {
      const shouldHideContent = kindConfig.hideIfZero && line[kindConfig.valueKey] === 0;

      if (shouldHideContent && kindConfig.separator) {
        return (
          <BillingItem
            key={`${line.kind}-${index}`}
            label=""
            value={0}
            separatorType={index === 0 ? kindConfig.separator : 'simple'}
            indented={kindConfig.indented}
            hideContent={true}
            bold={kindConfig.bold}
          />
        );
      } else if (shouldHideContent) {
        return null;
      }

      const displayName = kindConfig.label || line.name;
      const truncatedLabel =
        displayName && typeof displayName === 'string'
          ? displayName.length > truncateLength
            ? displayName.slice(0, truncateLength) + '…'
            : displayName
          : displayName;

      return (
        <BillingItem
          key={`${line.kind}-${index}`}
          label={truncatedLabel}
          value={line[kindConfig.valueKey]}
          separatorType={index === 0 ? kindConfig.separator : undefined}
          indented={kindConfig.indented}
          bold={kindConfig.bold}
        />
      );
    });
  };

  return (
    <div>
      <h4 id='summarySuccess'>Billing</h4>

      {displayConfigs.map((config) => (
        <React.Fragment
          key={config._aggregatedInventoryTotal ? 'aggregated-inventory' : config.kind}
        >
          {renderBillingLinesForKind(config)}
        </React.Fragment>
      ))}
    </div>
  );
};

export default BillingLinesTable;
